import type {BoardColumn, BoardRow} from '@github-ui/board-core'
import {Banner} from '@primer/react'
import {type CSSProperties, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useVisibleFieldIdentifiers} from '../../../contexts/views/VisibleFieldsProvider'
import {useDashboardIssueSidePanel} from '../../../hooks/use-dashboard-issue-side-panel'
import {type FieldDefinition, ItemType, withAlwaysVisibleFields} from '../../saved-view-options/fields'
import {SavedViewBoard} from '../SavedViewBoard'
import type {BoardItemEdit} from '../board-document-adapter'
import {AgentSessionStatusProvider} from './agent-sessions/agent-session-statuses'
import {type AgentSessionPanelTarget, useAgentSessionPanel} from './agent-sessions/use-agent-session-panel'
import type {IssuesBoardMetadata} from './board-metadata'
import type {BoardPayloadItem} from './board-payload'
import {
  AssignedAgents,
  BoardAgentAssignmentProvider,
  useAgentAssignmentCardActions,
  useAgentAssignmentMenuIntent,
} from './BoardAgentAssignment'
import {applyOptimisticUpdateToCard} from './optimistic-updates'
import {projectIssueCardForVerification} from './optimistic-updates/verification'
import {BoardCard, type BoardFilter, type BoardItemIdentifier} from './BoardCard'
import {BoardCardActions} from './BoardCardActions'
import {BoardColumnHeading} from './BoardColumnHeading'
import {BoardRowHeading} from './BoardRowHeading'
import type {IssuesSavedViewBoardAdapter} from './card-actions'
import {
  useAnnounceDeferredCardFieldFailures,
  useInvalidateEditedDeferredCardFields,
  useInvalidateRefreshedDeferredCardFields,
  useLoadedDeferredCardFields,
} from './deferred-card-fields'
import {IssuesBoardBlankslate} from './IssuesBoardBlankslate'

const AgentSessionPanel = lazy(() => import('./agent-sessions/AgentSessionPanel'))

/**
 * Empty, so `j` and `k` in the side panel do nothing. The cards are paged into the board per
 * cell, and this host holds no ordered list of them to step through.
 */
const NO_NAVIGABLE_ISSUES: Parameters<typeof useDashboardIssueSidePanel>[0]['navigableIssues'] = []

/**
 * Refetching the route after a panel edit re-keys the board and drops every cell page loaded
 * since, and the index may not have the edit yet. Updating the card in place belongs with
 * github/views#105.
 */
const NO_REFRESH = () => {}

/**
 * A saved view's issues as a board served by the view's own endpoints. An issue title opens the
 * issue in the side panel, and a pull request title opens its own page.
 */
export function IssuesSavedViewBoard({
  adapter,
  query,
  isNavigating,
  fieldDefinitions,
  onQueryChange,
  savedQuery,
  viewportAnchor,
  boardClassName,
  readOnly = false,
}: {
  adapter: IssuesSavedViewBoardAdapter
  query: string
  /** The query the view has saved. Cards can be reordered only while `query` is this one. */
  savedQuery?: string
  /** Which custom fields are single-selects. Until it loads, cards draw no custom fields. */
  fieldDefinitions: readonly FieldDefinition[] | undefined
  /**
   * The route is fetching the response this board is built from. Applying a grouping or a
   * swimlane rebuilds both axes, so the board on screen is drawn as a placeholder until
   * the new one arrives. The same fact the list layout is handed as `isLoading`.
   */
  isNavigating: boolean
  /** Shows the view for a query, keeping its layout and other view parameters. */
  onQueryChange: (query: string) => void
  viewportAnchor?: 'document' | 'viewport'
  boardClassName?: string
  /**
   * Titles follow their links instead of opening the side panel, and cards take no edits. Moves
   * are off only when the adapter has no `moveBoardItem`.
   */
  readOnly?: boolean
}) {
  const {deferredFieldsSource, cardActionsSource} = adapter
  useAnnounceDeferredCardFieldFailures(deferredFieldsSource)
  useInvalidateEditedDeferredCardFields(deferredFieldsSource, fieldDefinitions)
  const onItemsRefreshed = useInvalidateRefreshedDeferredCardFields(deferredFieldsSource)
  const chosenFieldIdentifiers = useVisibleFieldIdentifiers()
  const visibleFieldIdentifiers = useMemo(
    () => withAlwaysVisibleFields(chosenFieldIdentifiers, fieldDefinitions),
    [chosenFieldIdentifiers, fieldDefinitions],
  )
  const loadDeferredCardFields = useLoadedDeferredCardFields(
    deferredFieldsSource,
    visibleFieldIdentifiers,
    fieldDefinitions,
  )
  const [assignedAgents] = useState(() => new AssignedAgents())
  const onLoadedItemsChange = useCallback(
    (items: readonly BoardPayloadItem[]) => {
      loadDeferredCardFields(items)
      assignedAgents.reconcile(items)
    },
    [assignedAgents, loadDeferredCardFields],
  )
  const {
    onIssueSelect,
    isSidePanelOpen,
    closeSidePanel,
    listContainerProps,
    focusListContainer,
    sidePanel,
    deletionErrorNotice,
  } = useDashboardIssueSidePanel({
    navigableIssues: NO_NAVIGABLE_ISSUES,
    refreshList: NO_REFRESH,
  })

  const isSidePanelOpenRef = useRef(isSidePanelOpen)
  useEffect(() => {
    isSidePanelOpenRef.current = isSidePanelOpen
  }, [isSidePanelOpen])
  // Reads the open state from a ref so toggling the panel does not redraw every card.
  const closeIssueSidePanel = useCallback(() => {
    if (isSidePanelOpenRef.current) closeSidePanel()
  }, [closeSidePanel])

  const {
    target: agentSessionTarget,
    open: openAgentSessionPanel,
    close: closeAgentSessionPanel,
    returnFocusRef: agentSessionReturnFocusRef,
  } = useAgentSessionPanel()
  // One side panel at a time: opening a session closes the issue, and opening an issue closes the session.
  const onAgentSessionOpen = useCallback(
    (target: AgentSessionPanelTarget, returnFocusElement: HTMLElement) => {
      closeIssueSidePanel()
      openAgentSessionPanel(target, returnFocusElement)
    },
    [closeIssueSidePanel, openAgentSessionPanel],
  )

  // A session panel opened from a URL or history has no pill to return focus to, and a pill can be
  // redrawn while it is open. Focus the board instead of leaving it on the page body (WCAG 2.4.3).
  const isAgentSessionPanelOpen = agentSessionTarget !== null
  useEffect(() => {
    if (!isAgentSessionPanelOpen) return
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- read on close: the opener is set after this effect runs
      if (agentSessionReturnFocusRef.current?.isConnected || document.activeElement !== document.body) return
      focusListContainer()
    }
  }, [isAgentSessionPanelOpen, agentSessionReturnFocusRef, focusListContainer])

  const onItemSelect = useCallback(
    (itemType: ItemType, item: BoardItemIdentifier, returnFocusElement: HTMLElement) => {
      switch (itemType) {
        case ItemType.ISSUE:
          closeAgentSessionPanel()
          onIssueSelect(item, returnFocusElement)
          return true
        case ItemType.PULL_REQUEST:
          return false
        default: {
          const _exhaustive: never = itemType
          return _exhaustive
        }
      }
    },
    [closeAgentSessionPanel, onIssueSelect],
  )

  const filter = useMemo<BoardFilter>(() => ({query, apply: onQueryChange}), [onQueryChange, query])

  // A side-panel edit updates the card where it is.
  const applyItemEdit = useCallback(
    (item: BoardPayloadItem, edit: BoardItemEdit) => applyOptimisticUpdateToCard(item, edit, fieldDefinitions ?? []),
    [fieldDefinitions],
  )

  const renderCard = useCallback(
    (item: BoardPayloadItem) => (
      <BoardCard
        item={item}
        visibleFieldIdentifiers={visibleFieldIdentifiers}
        fieldDefinitions={fieldDefinitions}
        onItemSelect={readOnly ? undefined : onItemSelect}
        filter={filter}
        deferredFieldsSource={deferredFieldsSource}
        onAgentSessionOpen={onAgentSessionOpen}
      />
    ),
    [
      visibleFieldIdentifiers,
      fieldDefinitions,
      readOnly,
      onItemSelect,
      filter,
      deferredFieldsSource,
      onAgentSessionOpen,
    ],
  )

  const renderColumnHeader = useCallback(
    (column: BoardColumn, metadata: IssuesBoardMetadata) => (
      <BoardColumnHeading column={column} labels={metadata.columnLabels} />
    ),
    [],
  )

  const renderRowHeader = useCallback(
    (row: BoardRow, metadata: IssuesBoardMetadata) => <BoardRowHeading row={row} labels={metadata.rowLabels} />,
    [],
  )

  const renderEmptyState = useCallback(
    (metadata: IssuesBoardMetadata) => <IssuesBoardBlankslate query={query} metadata={metadata} />,
    [query],
  )

  const renderAgentAssignment = useAgentAssignmentCardActions(assignedAgents, cardActionsSource)
  const onCardMenuIntent = useAgentAssignmentMenuIntent(assignedAgents, cardActionsSource)
  const renderCardActions = useCallback((item: BoardPayloadItem) => <BoardCardActions permalink={item.permalink} />, [])

  return (
    <AgentSessionStatusProvider>
      <BoardAgentAssignmentProvider assignedAgents={assignedAgents}>
        {deletionErrorNotice && (
          <Banner
            variant="critical"
            title={deletionErrorNotice.title}
            hideTitle
            description={deletionErrorNotice.message}
            className="tmp-mt-3"
          />
        )}
        {/* The side panel focuses this region when it closes and the clicked title is gone. */}
        <div {...listContainerProps}>
          <SavedViewBoard
            adapter={adapter}
            // The query the reader is looking at, which the route sends as the view's own when the
            // URL carries none. It identifies the board's cache rather than the request, so date
            // tokens stay unresolved: the server resolves the query it was sent, and a key naming
            // something else would claim a request that was never made.
            options={{query}}
            renderCard={renderCard}
            renderCardActions={renderCardActions}
            renderCardTrailingActions={renderAgentAssignment}
            onCardMenuIntent={onCardMenuIntent}
            // `BoardCard` renders `BoardCardMenuButton`, and a card without one has no menu button.
            cardMenuPlacement="inline"
            applyItemEdit={readOnly ? undefined : applyItemEdit}
            projectItemForVerification={projectIssueCardForVerification}
            onLoadedItemsChange={onLoadedItemsChange}
            onItemsRefreshed={onItemsRefreshed}
            renderColumnHeader={renderColumnHeader}
            renderRowHeader={renderRowHeader}
            renderEmptyState={renderEmptyState}
            isLoading={isNavigating}
            savedQuery={savedQuery}
            viewportAnchor={viewportAnchor}
            boardClassName={boardClassName}
          />
        </div>
        {readOnly ? null : sidePanel}
        {agentSessionTarget && (
          <Suspense fallback={null}>
            <AgentSessionPanel
              target={agentSessionTarget}
              onClose={closeAgentSessionPanel}
              returnFocusRef={agentSessionReturnFocusRef}
            />
          </Suspense>
        )}
      </BoardAgentAssignmentProvider>
    </AgentSessionStatusProvider>
  )
}

export interface SavedViewBoardLayout {
  /** Spacing around the board, and around the placeholder drawn while it loads. */
  containerStyle: CSSProperties
  className: string
  boardClassName: string
}
